import {
  deleteField,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import {
  DEFAULT_SPRITE_COLORS,
  type AvatarFormValues,
  type UserProfile,
} from '../../../types/user';
import { getFirebaseDb } from '../client';
import {
  toAvatarLayerSelection,
  toAvatarMode,
  toHexColor,
  toOptionalString,
  toTimestampDate,
} from './documentParsers';

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const db = getFirebaseDb();
  const snapshot = await getDoc(doc(db, 'users', uid));
  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();
  const spriteColorsRaw = (data.spriteColors as Record<string, unknown> | undefined) ?? {};
  const isAnonymous = data.isAnonymous === true;
  const displayName = String(data.displayName ?? '').trim();

  return {
    displayName: displayName || (isAnonymous ? 'Explorer' : ''),
    bio: String(data.bio ?? ''),
    hasCompletedCharacterSetup: data.hasCompletedCharacterSetup === true,
    spriteId: String(data.spriteId ?? 'hero_01'),
    spriteColors: {
      hair: toHexColor(spriteColorsRaw.hair, DEFAULT_SPRITE_COLORS.hair),
      clothing: toHexColor(spriteColorsRaw.clothing, DEFAULT_SPRITE_COLORS.clothing),
    },
    avatarLayers: toAvatarLayerSelection(data.avatarLayers),
    avatarMode: toAvatarMode(data.avatarMode),
    customAvatarId: toOptionalString(data.customAvatarId),
    isAnonymous,
    isApproved: data.isApproved === true,
    role: data.role === 'admin' ? 'admin' : 'user',
    createdAt: toTimestampDate(data.createdAt),
    lastSeen: toTimestampDate(data.lastSeen),
  };
};

export const upsertUserProfile = async (
  uid: string,
  isAnonymous: boolean,
  payload: AvatarFormValues,
): Promise<void> => {
  const db = getFirebaseDb();
  const userRef = doc(db, 'users', uid);

  await runTransaction(db, async (transaction) => {
    const existingDoc = await transaction.get(userRef);
    const isNewUser = !existingDoc.exists();

    transaction.set(
      userRef,
      {
        ...payload,
        bio: payload.bio.trim(),
        hasCompletedCharacterSetup: true,
        backstory: deleteField(),
        personality: deleteField(),
        personalitySpec: deleteField(),
        spriteColors: {
          hair: payload.spriteColors.hair,
          clothing: payload.spriteColors.clothing,
        },
        avatarLayers: payload.avatarLayers
          ? {
              body: payload.avatarLayers.body,
              eyes: payload.avatarLayers.eyes,
              hairstyle: payload.avatarLayers.hairstyle,
              outfit: payload.avatarLayers.outfit,
              accessory: payload.avatarLayers.accessory,
              prop: payload.avatarLayers.prop,
            }
          : undefined,
        avatarMode: payload.avatarMode === 'custom' ? 'custom' : 'layers',
        customAvatarId: payload.customAvatarId?.trim() || null,
        isAnonymous,
        lastSeen: serverTimestamp(),
        ...(isNewUser ? { createdAt: serverTimestamp() } : {}),
      },
      { merge: true },
    );
  });
};

export const updateUserLastSeen = async (uid: string): Promise<void> => {
  const db = getFirebaseDb();
  await setDoc(doc(db, 'users', uid), { lastSeen: serverTimestamp() }, { merge: true });
};
